/**
 * ProfileTemplates.ts - Pre-built Profile Templates
 * 
 * CHANGE HISTORY:
 * - 251102_1647 danielbeer: Create profile template library
 * 
 * Ready-to-use profile templates for common use cases
 */

import type { Profile } from '../types';

/**
 * Profile template (without id, createdAt, updatedAt)
 */
export type ProfileTemplate = Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * Marketing Messaging Profile
 * For creating marketing content and messaging
 */
export const MARKETING_MESSAGING: ProfileTemplate = {
  name: 'marketing-messaging',
  description: 'Marketing messaging and content generation',
  tags: ['marketing', 'content', 'public'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['marketing-kb', 'brand-guidelines', 'approved-messaging'],
    excludeDatasets: ['internal-only', 'confidential'],
    tags: ['public', 'approved', 'marketing'],
    dateRange: { start: '2024-01-01' }
  },
  
  perspective: {
    tone: 'professional-friendly',
    audience: 'B2B decision makers',
    style: 'conversational',
    language: 'en-US'
  },
  
  context: {
    industry: 'technology',
    focus: 'value-proposition',
    brandVoice: 'innovative-trustworthy'
  },
  
  access: {
    read: ['all'],
    edit: ['marketing-team', 'content-team'],
    approve: ['marketing-director'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'openai',
    llmModel: 'gpt-4-turbo',
    temperature: 0.7,
    maxTokens: 1000,
    enableCache: true,
    requireApproval: true
  }
};

/**
 * GTM Planning Profile
 * For go-to-market planning and strategy
 */
export const GTM_PLANNING: ProfileTemplate = {
  name: 'gtm-planning',
  description: 'Go-to-market planning and strategic analysis',
  tags: ['gtm', 'planning', 'strategic'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['market-analysis', 'competition-data', 'target-markets', 'sales-data'],
    tags: ['gtm', 'planning', 'strategic'],
    dateRange: { start: '2024-01-01' }
  },
  
  perspective: {
    tone: 'analytical-strategic',
    audience: 'internal-stakeholders',
    focus: 'strategic'
  },
  
  context: {
    industry: 'technology',
    focus: 'market-entry',
    competitiveAnalysis: true
  },
  
  access: {
    read: ['sales-team', 'marketing-team', 'leadership'],
    edit: ['gtm-team', 'strategy-team'],
    approve: ['vp-sales', 'vp-marketing'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'anthropic',
    llmModel: 'claude-3-opus',
    temperature: 0.3,
    maxTokens: 2000,
    enableCache: true,
    requireApproval: true
  }
};

/**
 * Proposal Generation Profile
 * For creating sales proposals and RFP responses
 */
export const PROPOSAL_GENERATION: ProfileTemplate = {
  name: 'proposal-generation',
  description: 'Sales proposal and RFP response generation',
  tags: ['sales', 'proposals', 'rfp'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['proposal-templates', 'case-studies', 'pricing-data', 'client-research'],
    tags: ['proposals', 'sales', 'approved']
  },
  
  perspective: {
    tone: 'professional-persuasive',
    audience: 'client-decision-makers',
    focus: 'value-proposition',
    style: 'formal'
  },
  
  context: {
    industry: 'technology',
    focus: 'solution-selling',
    includeROI: true,
    includeCaseStudies: true
  },
  
  access: {
    read: ['sales-team', 'proposal-team'],
    edit: ['sales-team', 'proposal-team'],
    approve: ['sales-director'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'openai',
    llmModel: 'gpt-4-turbo',
    temperature: 0.6,
    maxTokens: 3000,
    enableCache: true,
    requireApproval: true,
    autoPopulateTemplates: true
  }
};

/**
 * Internal Brainstorming Profile
 * For creative internal ideation and exploration
 */
export const INTERNAL_BRAINSTORMING: ProfileTemplate = {
  name: 'internal-brainstorming',
  description: 'Creative internal brainstorming and ideation',
  tags: ['internal', 'creative', 'brainstorming'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['all'],
    excludeDatasets: ['confidential-hr', 'financial-sensitive'],
    tags: ['internal']
  },
  
  perspective: {
    tone: 'creative-exploratory',
    audience: 'internal-team',
    style: 'informal',
    focus: 'innovation'
  },
  
  context: {
    industry: 'technology',
    focus: 'innovation',
    experimentalIdeas: true
  },
  
  access: {
    read: ['all-employees'],
    edit: ['all-employees'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'openai',
    llmModel: 'gpt-4-turbo',
    temperature: 0.9,
    maxTokens: 1500,
    enableCache: false,
    requireApproval: false,
    enableExperimental: true
  }
};

/**
 * Customer Support Profile
 * For customer support and help desk responses
 */
export const CUSTOMER_SUPPORT: ProfileTemplate = {
  name: 'customer-support',
  description: 'Customer support and help desk assistance',
  tags: ['support', 'customer-service', 'help-desk'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['help-docs', 'kb-articles', 'troubleshooting-guides', 'product-docs'],
    tags: ['support', 'public', 'approved'],
    dateRange: { start: '2024-01-01' }
  },
  
  perspective: {
    tone: 'helpful-empathetic',
    audience: 'customers',
    style: 'clear-concise',
    focus: 'problem-solving'
  },
  
  context: {
    industry: 'technology',
    focus: 'customer-success',
    includeSteps: true,
    includeExamples: true
  },
  
  access: {
    read: ['support-team', 'all-employees'],
    edit: ['support-team', 'product-team'],
    approve: ['support-manager'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'openai',
    llmModel: 'gpt-4-turbo',
    temperature: 0.5,
    maxTokens: 800,
    enableCache: true,
    requireApproval: false
  }
};

/**
 * Technical Documentation Profile
 * For creating technical documentation and API docs
 */
export const TECHNICAL_DOCUMENTATION: ProfileTemplate = {
  name: 'technical-documentation',
  description: 'Technical documentation and API reference',
  tags: ['technical', 'documentation', 'api'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['api-specs', 'code-examples', 'architecture-docs', 'technical-guides'],
    tags: ['technical', 'documentation', 'approved']
  },
  
  perspective: {
    tone: 'technical-precise',
    audience: 'developers',
    style: 'formal-detailed',
    focus: 'accuracy'
  },
  
  context: {
    industry: 'technology',
    focus: 'technical-accuracy',
    includeCodeExamples: true,
    includeAPIReferences: true
  },
  
  access: {
    read: ['all-employees', 'partners'],
    edit: ['engineering-team', 'technical-writers'],
    approve: ['engineering-manager'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'anthropic',
    llmModel: 'claude-3-opus',
    temperature: 0.2,
    maxTokens: 2500,
    enableCache: true,
    requireApproval: true
  }
};

/**
 * Social Media Profile
 * For creating social media content
 */
export const SOCIAL_MEDIA: ProfileTemplate = {
  name: 'social-media',
  description: 'Social media content creation',
  tags: ['social-media', 'marketing', 'public'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['social-content', 'brand-guidelines', 'trending-topics'],
    tags: ['social', 'public', 'approved'],
    dateRange: { start: '2024-01-01' }
  },
  
  perspective: {
    tone: 'engaging-conversational',
    audience: 'social-media-followers',
    style: 'casual-friendly',
    focus: 'engagement'
  },
  
  context: {
    industry: 'technology',
    focus: 'brand-awareness',
    includeHashtags: true,
    includeEmojis: true
  },
  
  access: {
    read: ['marketing-team', 'social-media-team'],
    edit: ['social-media-team'],
    approve: ['social-media-manager'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'openai',
    llmModel: 'gpt-4-turbo',
    temperature: 0.8,
    maxTokens: 500,
    enableCache: true,
    requireApproval: true
  }
};

/**
 * Executive Summary Profile
 * For creating executive summaries and board reports
 */
export const EXECUTIVE_SUMMARY: ProfileTemplate = {
  name: 'executive-summary',
  description: 'Executive summaries and board reports',
  tags: ['executive', 'leadership', 'strategic'],
  scope: 'app',
  
  dataBoundaries: {
    datasets: ['financial-data', 'performance-metrics', 'strategic-initiatives'],
    tags: ['executive', 'strategic', 'approved'],
    excludeDatasets: ['operational-details']
  },
  
  perspective: {
    tone: 'executive-concise',
    audience: 'c-level-board',
    style: 'formal-strategic',
    focus: 'high-level-insights'
  },
  
  context: {
    industry: 'technology',
    focus: 'strategic-outcomes',
    includeMetrics: true,
    includeRecommendations: true
  },
  
  access: {
    read: ['leadership', 'board'],
    edit: ['strategy-team', 'executive-team'],
    approve: ['ceo', 'cfo'],
    delete: ['admin']
  },
  
  behavior: {
    llmProvider: 'anthropic',
    llmModel: 'claude-3-opus',
    temperature: 0.3,
    maxTokens: 1500,
    enableCache: true,
    requireApproval: true
  }
};

/**
 * All available templates
 */
export const PROFILE_TEMPLATES = {
  MARKETING_MESSAGING,
  GTM_PLANNING,
  PROPOSAL_GENERATION,
  INTERNAL_BRAINSTORMING,
  CUSTOMER_SUPPORT,
  TECHNICAL_DOCUMENTATION,
  SOCIAL_MEDIA,
  EXECUTIVE_SUMMARY
};

/**
 * Get template by name
 */
export function getTemplate(name: string): ProfileTemplate | null {
  const key = name.toUpperCase().replace(/-/g, '_');
  return (PROFILE_TEMPLATES as any)[key] || null;
}

/**
 * List all template names
 */
export function listTemplates(): string[] {
  return Object.keys(PROFILE_TEMPLATES);
}

/**
 * Create profile from template with overrides
 */
export function createFromTemplate(
  templateName: string,
  overrides: Partial<ProfileTemplate> = {}
): ProfileTemplate {
  const template = getTemplate(templateName);
  if (!template) {
    throw new Error(`Template not found: ${templateName}`);
  }
  
  return {
    ...template,
    ...overrides,
    // Deep merge data boundaries
    dataBoundaries: {
      ...template.dataBoundaries,
      ...overrides.dataBoundaries
    },
    // Deep merge perspective
    perspective: {
      ...template.perspective,
      ...overrides.perspective
    },
    // Deep merge context
    context: {
      ...template.context,
      ...overrides.context
    },
    // Deep merge access
    access: {
      ...template.access,
      ...overrides.access
    },
    // Deep merge behavior
    behavior: {
      ...template.behavior,
      ...overrides.behavior
    }
  };
}

export default PROFILE_TEMPLATES;
